import { Component, ChangeDetectionStrategy, inject, AfterViewInit, ElementRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { FooterComponent } from '../footer/footer.component';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-maintenance',
  standalone: true,
  imports: [CommonModule, FooterComponent],
  template: `
    <main>
      <!-- HEADER: Exakt wie Imprint -->
      <div class="header-container">
        <div class="header-content">
          <a (click)="goHome()" style="cursor: pointer; text-decoration: none; display: inline-block;">
            <h1>ProTyco</h1>
            <p style="text-align: center; margin: 0.25rem 0 0 0;">
              <span style="color: oklch(51% 0.28 263.83);">PRO</span>PERTY <span style="color: oklch(51% 0.28 263.83);">TYCO</span>ON
            </p>
          </a>
        </div>
      </div>

      <!-- CONTENT: Nur DB-Text, großer Platz -->
      <div class="maintenance-scroll">
        <div class="container">
          <div class="maintenance-card">
            <!-- Sanitized HTML mit inline-styles -->
            <div class="maintenance-content" [innerHTML]="maintenanceStatusSignal().maintenanceText"></div>
          </div>
        </div>
      </div>
    </main>

    <!-- FOOTER: Exakt wie überall -->
    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0 0 1.5rem;
      min-height: 0;
    }

    .maintenance-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 22px 1.5rem 0;
    }

    /* ============ HEADER - Exakt von Imprint ============ */
    .header-container {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      background: white;
      color: white;
      padding: 1rem 2rem 1rem 2rem;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      border-bottom: 1px solid #d1d5db;
      position: relative;
      z-index: 10;
      overflow: visible;
    }

    /* Top Trennlinie wie bei Imprint */
    .header-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      pointer-events: none;
    }

    .header-content {
      text-align: center;
      position: relative;
      overflow: visible;
    }

    .header-content::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 40px;
      background: linear-gradient(135deg, oklch(60% 0.3 280) 0%, oklch(45% 0.28 263.83) 100%);
      border-radius: 50%;
      opacity: 0.1;
      filter: blur(20px);
      z-index: 0;
    }

    .header-content h1 {
      background: linear-gradient(135deg, oklch(45% 0.28 263.83) 0%, oklch(60% 0.3 280) 50%, oklch(45% 0.28 263.83) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-size: 3rem;
      margin: 0;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 900;
      letter-spacing: 0px;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(calc(-50% + 7px));
      white-space: nowrap;
    }

    .header-content p {
      color: oklch(28% 0.06 263.83);
      margin: 43px 0 0 15px !important;
      padding-bottom: 0rem;
      font-weight: 600;
      font-size: 0.875rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      line-height: 1.4;
    }

    .header-content p span {
      position: relative;
    }

    .header-content a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    /* ============ CONTENT ============ */
    .container {
      flex: 1;
      max-width: 1600px;
      margin: 0 auto;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .maintenance-card {
      background-color: transparent;
      border: none;
      border-radius: 0.5rem;
      padding: 0;
      box-shadow: none;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    .maintenance-content {
      width: 100%;
      line-height: 1.8;
      color: inherit;
      font-size: 1rem;
    }

    /* ============ RESPONSIVE ============ */
    @media (max-width: 768px) {
      .header-content h1 {
        font-size: 2rem;
        padding-top: 1rem;
      }

      .header-content p {
        font-size: 0.5625rem;
        margin: 32px 0 0 15px !important;
      }
    }

    @media (max-width: 640px) {
      main {
        padding: 0 0 1rem;
        gap: 0;
      }

      .maintenance-scroll {
        padding: 22px 0.375rem 0;
      }

      .maintenance-card {
        padding: 0;
        min-height: 300px;
      }

      .maintenance-content {
        font-size: 0.95rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MaintenanceComponent implements AfterViewInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);
  protected config = inject(ConfigService);
  
  // Convert Observable to Signal with DomSanitizer
  protected maintenanceStatusSignal = toSignal(
    this.config.getMaintenanceStatus().pipe(
      map(status => ({
        ...status,
        maintenanceText: this.sanitizer.bypassSecurityTrustHtml(status.maintenanceText)
      }))
    ),
    { initialValue: { isActive: false, maintenanceText: this.sanitizer.bypassSecurityTrustHtml('') as SafeHtml } }
  );

  ngAfterViewInit() {
    setTimeout(() => {
      window.scrollTo(0, 0);
      const scrollElement = this.elementRef.nativeElement.querySelector('.maintenance-scroll');
      if (scrollElement) {
        scrollElement.scrollTop = 0;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
  }

  goHome() {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/calculator']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
